import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ImageIcon,
  Loader2,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import type { Language } from '@/types';
import {
  deleteQCReport,
  fetchQCReports,
  fetchSiteVisits,
  type QCReportRow,
  type QCVisitOption,
} from '../api';
import { QCPhotoGrid } from './QCPhotoGrid';
import { QCReportFormDialog } from './QCReportFormDialog';

interface QCSiteViewProps {
  siteId: string;
  language: Language;
  onBack: () => void;
}

type SiteDetail = {
  id: string;
  name: string;
  address: string;
  customer_number: string | null;
};

const copy = {
  en: {
    back: '← Sites',
    conductQc: 'Conduct QC',
    noRecent: 'No visits in the last 30 days',
    previousVisits: 'Previous cleanings (last 30 days)',
    previousQcs: 'Previous QCs',
    noQcs: 'No QCs yet.',
    info: 'Info',
    totalThisYear: 'Total QCs this year',
    avgThisYear: 'Average score this year',
    lastQcDate: 'Last QC date',
    blocks: 'blocks',
    extrasOne: 'extra',
    extrasMany: 'extras',
    photos: 'photos',
    photosOne: 'photo',
    pass: 'Passed',
    fail: 'Failed',
    inspectorBy: 'by',
    delete: 'Delete',
    confirmDelete: 'Delete this QC report?',
    loading: 'Loading…',
    none: '—',
    unassigned: 'Unassigned',
    cleaner: 'Cleaner',
    blocksRated: 'blocks rated',
    avgVisit: 'Avg',
    noNotes: 'No notes',
    close: 'Close',
    viewQc: 'QC details',
    status: {
      completed: 'Completed',
      approved: 'Approved',
      assigned: 'Assigned',
      planned: 'Planned',
      cancelled: 'Cancelled',
    },
  },
  sv: {
    back: '← BRF:er',
    conductQc: 'Genomför QC',
    noRecent: 'Inga besök de senaste 30 dagarna',
    previousVisits: 'Senaste städningar (30 dagar)',
    previousQcs: 'Tidigare QC',
    noQcs: 'Inga QC än.',
    info: 'Info',
    totalThisYear: 'Antal QC i år',
    avgThisYear: 'Snittpoäng i år',
    lastQcDate: 'Senaste QC',
    blocks: 'moment',
    extrasOne: 'extra',
    extrasMany: 'extra',
    photos: 'bilder',
    photosOne: 'bild',
    pass: 'Godkänd',
    fail: 'Underkänd',
    inspectorBy: 'av',
    delete: 'Ta bort',
    confirmDelete: 'Ta bort denna QC-rapport?',
    loading: 'Laddar…',
    none: '—',
    unassigned: 'Ej tilldelad',
    cleaner: 'Städare',
    blocksRated: 'moment betygsatta',
    avgVisit: 'Snitt',
    noNotes: 'Inga anteckningar',
    close: 'Stäng',
    viewQc: 'QC-detaljer',
    status: {
      completed: 'Klar',
      approved: 'Godkänd',
      assigned: 'Tilldelad',
      planned: 'Planerad',
      cancelled: 'Avbruten',
    },
  },
} as const;

function StarRow({ score }: { score: 1 | 2 | 3 | 4 | 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={
            n <= score
              ? 'h-4 w-4 fill-primary text-primary'
              : 'h-4 w-4 text-muted-foreground/40'
          }
        />
      ))}
    </div>
  );
}

type SiteViewCopy = typeof copy['en'] | typeof copy['sv'];

function statusLabel(status: string, text: SiteViewCopy): string {
  return (text.status as Record<string, string>)[status] ?? status;
}

function describeBlocks(visit: QCVisitOption, text: SiteViewCopy): string {
  const recurringCount = Math.max(0, visit.block_count - visit.extras_count);
  const parts: string[] = [];
  parts.push(`${visit.block_count} ${text.blocks}`);
  const preview = visit.block_labels.slice(0, 2);
  if (preview.length > 0) {
    parts.push(preview.join(', '));
  }
  if (visit.extras_count > 0) {
    const extrasLabel = visit.extras_count === 1 ? text.extrasOne : text.extrasMany;
    parts.push(`+${visit.extras_count} ${extrasLabel}`);
  }
  // Suppress unused warning
  void recurringCount;
  return parts.join(' · ');
}

export function QCSiteView({ siteId, language, onBack }: QCSiteViewProps) {
  const text = copy[language];
  const { session, role } = useAuth();
  const token = session?.access_token ?? null;
  const isAdmin = role === 'admin';

  const [site, setSite] = useState<SiteDetail | null>(null);
  const [visits, setVisits] = useState<QCVisitOption[]>([]);
  const [reports, setReports] = useState<QCReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [openReportJobId, setOpenReportJobId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const loadAll = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const [siteRes, nextVisits, nextReports] = await Promise.all([
        supabase
          .from('sites')
          .select('id, name, address, customer_number')
          .eq('id', siteId)
          .maybeSingle(),
        fetchSiteVisits(token, siteId, 30),
        fetchQCReports(token, { siteId }),
      ]);

      if (siteRes.error) throw siteRes.error;
      setSite(siteRes.data
        ? {
            id: siteRes.data.id,
            name: siteRes.data.name,
            address: siteRes.data.address ?? '',
            customer_number: siteRes.data.customer_number ?? null,
          }
        : null);
      setVisits(nextVisits);
      setReports(nextReports);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, token]);

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (!window.confirm(text.confirmDelete)) return;
    setDeletingId(id);
    try {
      await deleteQCReport(token, id);
      setReports((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const groupedReports = useMemo(() => {
    type Group = {
      job_id: string;
      job_scheduled_for: string;
      cleaner_name: string | null;
      inspector_name: string | null;
      latest_inspected_at: string;
      avg: number;
      passed: boolean;
      reports: QCReportRow[];
    };
    const byJob = new Map<string, QCReportRow[]>();
    for (const r of reports) {
      const list = byJob.get(r.job_id) ?? [];
      list.push(r);
      byJob.set(r.job_id, list);
    }
    const groups: Group[] = [];
    for (const [jobId, list] of byJob) {
      const sorted = [...list].sort((a, b) =>
        b.inspected_at.localeCompare(a.inspected_at),
      );
      const avg = sorted.reduce((sum, r) => sum + r.score, 0) / sorted.length;
      groups.push({
        job_id: jobId,
        job_scheduled_for: sorted[0].job_scheduled_for,
        cleaner_name: sorted[0].cleaner_name,
        inspector_name: sorted[0].inspector_name,
        latest_inspected_at: sorted[0].inspected_at,
        avg,
        passed: avg >= 3,
        reports: sorted,
      });
    }
    groups.sort((a, b) =>
      b.latest_inspected_at.localeCompare(a.latest_inspected_at),
    );
    return groups;
  }, [reports]);

  const stats = useMemo(() => {
    const year = new Date().getFullYear();
    const yearReports = reports.filter((r) => r.inspected_at.startsWith(String(year)));
    const avg =
      yearReports.length === 0
        ? null
        : yearReports.reduce((sum, r) => sum + r.score, 0) / yearReports.length;
    const last = reports.length > 0 ? reports[0] : null;
    return {
      yearCount: yearReports.length,
      yearAvg: avg,
      lastInspectedAt: last?.inspected_at ?? null,
    };
  }, [reports]);

  const canConductQc = visits.length > 0;

  return (
    <div className="space-y-6 pb-6">
      <div>
        <Button type="button" variant="ghost" size="sm" onClick={onBack} className="-ml-3">
          <ArrowLeft className="mr-1 h-4 w-4" />
          {text.back}
        </Button>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {site?.name ?? (isLoading ? text.loading : '—')}
          </h1>
          {site?.address && (
            <p className="text-sm text-muted-foreground">
              {site.address}
              {site.customer_number ? ` · #${site.customer_number}` : ''}
            </p>
          )}
        </div>
        <Button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          disabled={!canConductQc || isLoading}
        >
          <Plus className="h-4 w-4" />
          <span className="ml-1">{text.conductQc}</span>
        </Button>
      </header>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Section 1: previous cleanings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{text.previousVisits}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {text.loading}
            </div>
          ) : visits.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">{text.noRecent}</div>
          ) : (
            <ul className="divide-y">
              {visits.map((visit) => {
                const expanded = expandedJobId === visit.job_id;
                const photoLabel = visit.photo_count === 1 ? text.photosOne : text.photos;
                return (
                  <li key={visit.job_id}>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedJobId(expanded ? null : visit.job_id)
                      }
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30"
                    >
                      {expanded ? (
                        <ChevronDown className="h-4 w-4 shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0" />
                      )}
                      <div className="flex-1 space-y-0.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs">{visit.scheduled_for}</span>
                          <span className="text-sm font-medium">
                            {visit.cleaner_name ?? text.unassigned}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {statusLabel(visit.status, text)}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {describeBlocks(visit, text)}
                        </div>
                      </div>
                      <Badge variant="outline" className="shrink-0 gap-1">
                        <ImageIcon className="h-3 w-3" />
                        {visit.photo_count} {photoLabel}
                      </Badge>
                    </button>
                    {expanded && (
                      <div className="px-4 pb-4">
                        <QCPhotoGrid
                          jobId={visit.job_id}
                          token={token}
                          language={language}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Section 2: previous QCs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{text.previousQcs}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {text.loading}
            </div>
          ) : groupedReports.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">{text.noQcs}</div>
          ) : (
            <ul className="divide-y">
              {groupedReports.map((group) => {
                const avgRounded = Math.round(group.avg) as 1 | 2 | 3 | 4 | 5;
                return (
                  <li key={group.job_id}>
                    <button
                      type="button"
                      onClick={() => setOpenReportJobId(group.job_id)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/30"
                    >
                      <ChevronRight className="h-4 w-4 shrink-0" />
                      <div className="flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs">
                            {group.job_scheduled_for || group.latest_inspected_at.slice(0, 10)}
                          </span>
                          <span className="text-sm font-medium">
                            {group.cleaner_name ?? text.unassigned}
                          </span>
                          <StarRow score={avgRounded} />
                          <span className="text-xs text-muted-foreground">
                            {text.avgVisit}: {group.avg.toFixed(2)}
                          </span>
                          <Badge variant={group.passed ? 'success' : 'destructive'}>
                            {group.passed ? text.pass : text.fail}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {group.reports.length} {text.blocksRated}
                          {group.inspector_name ? ` · ${text.inspectorBy} ${group.inspector_name}` : ''}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Section 3: info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{text.info}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {text.totalThisYear}
              </div>
              <div className="text-xl font-semibold">{stats.yearCount}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {text.avgThisYear}
              </div>
              <div className="text-xl font-semibold">
                {stats.yearAvg == null ? text.none : stats.yearAvg.toFixed(1)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {text.lastQcDate}
              </div>
              <div className="text-xl font-semibold">
                {stats.lastInspectedAt ? stats.lastInspectedAt.slice(0, 10) : text.none}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {site && (
        <QCReportFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          siteId={site.id}
          siteName={site.name}
          token={token}
          language={language}
          onCreated={() => void loadAll()}
        />
      )}

      {(() => {
        const openGroup = groupedReports.find((g) => g.job_id === openReportJobId);
        return (
          <Dialog
            open={Boolean(openGroup)}
            onOpenChange={(next) => !next && setOpenReportJobId(null)}
          >
            <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden p-0">
              <DialogHeader className="px-6 pt-6">
                <DialogTitle>{text.viewQc}</DialogTitle>
                <DialogDescription>
                  {openGroup
                    ? `${openGroup.job_scheduled_for || openGroup.latest_inspected_at.slice(0, 10)} · ${openGroup.cleaner_name ?? text.unassigned} · ${text.avgVisit}: ${openGroup.avg.toFixed(2)}`
                    : ''}
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                {openGroup ? (
                  <div className="space-y-3">
                    {openGroup.reports.map((report) => (
                      <div key={report.id} className="rounded-md border p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="space-y-1">
                            <div className="font-medium">
                              {report.job_block_label ?? text.none}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {report.inspected_at.slice(0, 10)}
                              {' · '}
                              {text.inspectorBy} {report.inspector_name ?? '—'}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <StarRow score={report.score} />
                            <Badge variant={report.passed ? 'success' : 'destructive'}>
                              {report.passed ? text.pass : text.fail}
                            </Badge>
                            {isAdmin && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => void handleDelete(report.id)}
                                disabled={deletingId === report.id}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                {deletingId === report.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                                <span className="sr-only">{text.delete}</span>
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="mt-2 text-sm">
                          {report.notes ?? (
                            <span className="text-muted-foreground">{text.noNotes}</span>
                          )}
                        </p>
                        {report.job_block_id && (
                          <div className="mt-3">
                            <QCPhotoGrid
                              jobId={report.job_id}
                              blockId={report.job_block_id}
                              token={token}
                              language={language}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <DialogFooter className="border-t bg-background px-6 py-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpenReportJobId(null)}
                >
                  {text.close}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}
